import React, { useState, useCallback, useContext } from 'react';
import { FORM_ERROR } from 'final-form';
import { useHistory } from 'react-router-dom';
import * as yup from 'yup';
import { CircularProgress } from '@material-ui/core';
import { AuthContext } from '@ryanar/react-auth-provider';
import { observer } from 'mobx-react';
import Form from 'ui/Form/Form';
import Button from 'ui/Button/Button';
import Input, { PasswordInput } from 'ui/Input';
import Link from 'ui/Link/Link';
import routePaths from 'routes/routePaths';
import api from 'api/api';
import auth from 'utils/auth';
import { ILoginBody, IOtpResponse } from 'utils/models';
import { mapYupObjectError, objectMap } from 'utils/helpers';
import { onlyAlphaNumeric } from 'utils/formatters';
import { DEVICE_ID, LONGITUDE, LATITUDE, DEVICE_MODEL, NETWORK_REQUIRED } from 'utils/constants';
import { OTPVerificationModal } from 'components/modals';
import { FormType } from 'components/modals/OTPVerificationModal/OTPVerificationModal';
import { ILogin, ILoginError, ILoginResponse, IOTPError } from './login.model';

import styles from './LogIn.module.css';

const extraValues = {
  deviceID: DEVICE_ID,
  longitude: LONGITUDE,
  latitude: LATITUDE,
  deviceModel: DEVICE_MODEL,
  isRootConsented: true,
};

const schema = yup.object().shape({
  loginID: yup.string().required('Username is required'),
  password: yup.string().required('Password is required'),
});

const LogIn = () => {
  const { setAuthenticated } = useContext(AuthContext);
  const history = useHistory();

  const [accountData, setAccountData] = useState<ILoginResponse | null>(null);
  const [isOTPModalOpen, setOTPModalOpen] = useState(false);
  const [OTPModalError, setOTPModalError] = useState<IOTPError | null>(null);

  const closeOTPModal = useCallback(() => {
    setOTPModalOpen(false);
  }, []);

  const validate = useCallback((values: ILogin) => {
    try {
      schema.validateSync(values, { abortEarly: false });
    } catch (e) {
      const valErrs = mapYupObjectError<ILogin>(e);
      return objectMap(valErrs, (v) => v[0]);
    }
    return undefined;
  }, []);

  const onOtpSubmit = useCallback(
    (otp: string | undefined, form: FormType | undefined) =>
      api
        .post(`customer/otp/verify/${otp || ''}/false/${accountData ? accountData.loginId : ''}`)
        .then((resp: IOtpResponse) => {
          if (!accountData || !resp.sessionId) {
            // eslint-disable-next-line no-throw-literal
            throw { description: 'Login was not successful, please try again.' };
          }
          auth.login({ ...accountData, sessionID: resp.sessionId });
          closeOTPModal();
          if (auth.data?.firstLogin || auth.data?.passwordChangeRequired) {
            history.push(routePaths.changePassword);
          } else {
            setAuthenticated(true);
            history.push(routePaths.dashboard);
          }
        })
        .catch((error: IOTPError) => {
          console.error(error);
          setOTPModalError(error);
          if (error.failureCount === 0 && form) {
            form.initialize({ digit1: '', digit2: '', digit3: '', digit4: '', digit5: '' });
          }
          return { [FORM_ERROR]: error.description || NETWORK_REQUIRED };
        }),
    [accountData, closeOTPModal, history, setAuthenticated],
  );

  const onResend = useCallback(
    (values: ILogin) => () => {
      const body: ILoginBody = {
        ...values,
        ...extraValues,
      };
      return api.post('customer/authenticate', body).then((response: ILoginResponse) => {
        setAccountData(response);
      });
    },
    [],
  );

  const onSubmit = useCallback((values: ILogin) => {
    const body: ILoginBody = {
      ...values,
      ...extraValues,
    };
    return (
      api
        // Change here for UAT-Prod encryption
        // .post('customer/authenticate', body, true, false)
        .post('customer/authenticate', body)
        .then((response: ILoginResponse) => {
          if (!response.loginId || !response.accountNumber || !response.email) {
            return Promise.reject({ code: 10, description: 'Login was not successful, please try again.' });
          }
          setAccountData(response);
          setOTPModalOpen(true);
          sessionStorage.setItem('firstName', response.fullname.split(' ')[0]);
          sessionStorage.setItem('lastName', response.fullname.split(' ')[1]);
          sessionStorage.setItem('fullName', response.fullname);
          sessionStorage.setItem('phoneNumber', response.phoneNumber);
          sessionStorage.setItem('accountNumber', response.accountNumber);
          sessionStorage.setItem('userName', response.loginId);

          return true;
        })
        .catch((error: ILoginError) => ({ [FORM_ERROR]: error.description || NETWORK_REQUIRED }))
    );
  }, []);
  return (
    <>
      <div className={styles.content}>
        <div className={styles.title}>Log In</div>
        <Form
          onSubmit={onSubmit}
          validate={validate}
          render={({ handleSubmit, values, submitting }) => (
            <form onSubmit={handleSubmit}>
              <Input
                name="loginID"
                label="Username"
                fieldProps={{
                  parse: onlyAlphaNumeric,
                }}
                disabled={submitting}
              />
              <PasswordInput name="password" label="Password" className={styles.password} disabled={submitting} />
              <Link to={routePaths.forgotPassword} className={styles.link}>
                Forgot Password?
              </Link>

              <Button type="submit" className={styles.submit} fullWidth disabled={submitting}>
                {submitting ? <CircularProgress size={20} color="inherit" /> : 'Log In'}
              </Button>
              <div className={styles.policy}>
                <Link to={routePaths.privacyPolicy}>Privacy Policy</Link>
                <span> and </span>
                <Link to={routePaths.regulatoryDirectives}>Regulatory Directives</Link>
              </div>
              <OTPVerificationModal
                open={isOTPModalOpen}
                onClose={closeOTPModal}
                onSubmit={onOtpSubmit}
                onResend={onResend(values)}
                OTPModalError={OTPModalError}
              />
            </form>
          )}
        />
      </div>
    </>
  );
};

export default observer(LogIn);
